import React, { useMemo, useState } from "react";

import moment from "moment";

import {
  ArrayUtils,
  formatDayDate,
  getAdminVerificationYearlyDataYear,
  getEditingRequestButtonState,
  useGetFieldPractices,
  useInView,
  useUpdateVerificationStatus,
} from "utils";

import { CROP_TYPE_FCPO, CropFCPO, FieldVerification, VerificationEditingRequest, VerificationStatus } from "models";

import { Button, Col, ContextMenu, Flex, Icon, IconButton, Message, Row, SkeletonWrapper, Spin, TagNormal, Tooltip } from "components";

import { CropDrawer } from "pages/admin/verifications/components/misc/CropDrawer";
import { EditingRequestModal } from "pages/admin/verifications/components/misc/EditingRequestModal";
import { VerificationStatusDropdown } from "pages/admin/verifications/components/misc/VerificationStatusDropdown";
import { CALENDAR_YEAR_START, FARM_YEAR_END, NEW_CROP } from "pages/app/all-field-view/constants/cropForm";

const formatYield = (yieldValue: number | null) => (!yieldValue ? "0 t/ha" : `${yieldValue} t/ha`);

export const CropTable = ({ field, farmId, editingRequests }: { field: FieldVerification; farmId: string; editingRequests: VerificationEditingRequest[] }) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const [editingRequestCrop, setEditingRequestCrop] = useState<CropFCPO | null>(null);
  const [missingCropRequestYear, setMissingCropRequestYear] = useState<number | null>(null);
  const [cropToEdit, setCropToEdit] = useState<CropFCPO | null>(null);

  const fieldId = field.id;

  const { data: fieldPractices, isLoading, isFetching } = useGetFieldPractices({ fieldId, inView, isAdmin: true });

  const { mutate: updateStatus } = useUpdateVerificationStatus({ fieldId, farmId });

  // Handler for updating crop verification status
  const handleStatusChange = (cropId: string, newStatus: VerificationStatus) => {
    if (!newStatus) return;

    updateStatus(
      {
        practiceType: "crops",
        practiceId: cropId,
        verificationStatus: newStatus,
      },
      {
        onSuccess: () => {
          Message({ icon: "tick", content: "Crop verification status updated successfully" });
        },
      }
    );
  };

  // Helper function to get editing requests for a specific crop
  const getCropEditingRequests = (cropId: string): VerificationEditingRequest[] => editingRequests.filter((request) => request.fieldCropId === cropId);

  // Helper function to get missing crop editing requests for a specific year
  const getMissingCropEditingRequests = (year: number): VerificationEditingRequest[] =>
    editingRequests.filter((request) => request.fieldCropId === null && request.year === year);

  // Memoize the leaf props to prevent re-rendering issues
  const leafPropsMap = useMemo(() => {
    const map = new Map<string, { showTooltip: boolean; isOrange: boolean }>();
    fieldPractices?.crops?.forEach((crop) => {
      if (crop.hasResidue) {
        const shouldShowTooltip = Math.random() < 1 / 50; // EasterEgg: the golden leaf has a 1/50 chance of magically appearing
        map.set(crop.id, {
          showTooltip: shouldShowTooltip,
          isOrange: shouldShowTooltip,
        });
      }
    });
    return map;
  }, [fieldPractices?.crops]);

  // Helper function to get leaf props
  const getLeafProps = (cropId: string) => leafPropsMap.get(cropId) || { showTooltip: false, isOrange: false };

  // Group crops by year
  const cropsByYear = ArrayUtils.groupBy(fieldPractices?.crops ?? [], (crop) => crop?.year ?? "unknown");

  // Inject verification year, if does not exist
  const verificationYear = getAdminVerificationYearlyDataYear();
  cropsByYear[verificationYear] = cropsByYear[verificationYear] ?? [];

  // Sort years in descending order
  const sortedYears = Object.keys(cropsByYear || {})
    .map(Number)
    .sort((a, b) => b - a);

  // Define crop type order: MAIN -> COVER -> UNDERSOWING
  const cropTypeOrder = [CROP_TYPE_FCPO.MAIN, CROP_TYPE_FCPO.COVER, CROP_TYPE_FCPO.UNDERSOWING];

  // Function to sort crops by type
  const sortCropsByType = (crops: NonNullable<typeof fieldPractices>["crops"]) =>
    crops.sort((a, b) => {
      const aIndex = cropTypeOrder.indexOf(a.cropType);
      const bIndex = cropTypeOrder.indexOf(b.cropType);
      return aIndex - bIndex;
    });

  // Sort crops within each year by crop type
  const sortedCropsByYear = Object.fromEntries(sortedYears.map((year) => [year, sortCropsByType([...cropsByYear![year]])]));

  return (
    <div ref={ref} className="crop-table">
      {isFetching && !isLoading && <RefetchingOverlay />}
      <SkeletonWrapper isLoading={isLoading} type="block" height="20rem">
        {/* Table Header */}
        <Row className="crop-table-header">
          <Col md={4} className="header-cell">
            crop name
          </Col>
          <Col md={2} className="header-cell">
            yield
          </Col>
          <Col md={3} className="header-cell">
            seeding start
          </Col>
          <Col md={3} className="header-cell">
            harvest end
          </Col>
          <Col md={3} className="header-cell">
            crop type
          </Col>
          <Col md={4} className="header-cell">
            status
          </Col>
          <Col md={2} className="header-cell">
            editing requests
          </Col>
          <Col md={3} className="header-cell">
            actions
          </Col>
        </Row>

        {/* Table Body grouped by year */}
        {sortedYears.map((year) => {
          const crops = sortedCropsByYear[year];
          return (
            <div key={year} className="year-group">
              {/* Year header */}
              <Row className="year-header" align="middle">
                <Col md={4}>
                  <Flex align="center">
                    <h4 className="year-title">{year}</h4>
                    <AddCropButton
                      onSelect={(cropType) =>
                        setCropToEdit(
                          NEW_CROP(cropType, moment(`${CALENDAR_YEAR_START}${year}`).toISOString(), moment(`${FARM_YEAR_END}${year}`).toISOString())
                        )
                      }
                    />
                  </Flex>
                </Col>
                <Col md={2}></Col>
                <Col md={3}></Col>
                <Col md={3}></Col>
                <Col md={3}></Col>
                <Col md={4}></Col>
                <Col md={2}>
                  <span className="paragraph-xs">
                    {(() => {
                      const missingCropRequests: VerificationEditingRequest[] = getMissingCropEditingRequests(year);
                      return `${missingCropRequests.filter((req) => req.completedAt).length}/${missingCropRequests.length}`;
                    })()}
                  </span>
                </Col>
                <Col md={3}>
                  {(() => {
                    const missingCropRequests = getMissingCropEditingRequests(year);
                    const buttonState = getEditingRequestButtonState(missingCropRequests);

                    return (
                      <Button type={buttonState.type} className={buttonState.className} onClick={() => setMissingCropRequestYear(year)}>
                        {buttonState.text}
                      </Button>
                    );
                  })()}
                </Col>
              </Row>

              {/* Crops for this year */}
              {crops.map((crop) => (
                <Row key={crop.id} className="crop-row pointer" onClick={() => setCropToEdit(crop)}>
                  <Col md={4} className="crop-cell">
                    <div className="crop-name-cell">
                      <div className={`crop-indicator ${crop.verificationStatus}`} />
                      <span className="paragraph-s-bold">{crop.cropName}</span>
                      {crop.hasResidue && (
                        <Tooltip
                          title={
                            getLeafProps(crop.id).showTooltip
                              ? "You found the Mattis Golden Leaf™ — rare item 🍃✨ Grants +10 residue insight and +5 verification power. (and yes this crop left residue)"
                              : ""
                          }
                        >
                          <Icon
                            iconName="tabler/leaf"
                            className={`icon inline margin-left-50 ${getLeafProps(crop.id).isOrange ? "orange-text" : "green-text"}`}
                            size="s"
                          />
                        </Tooltip>
                      )}
                    </div>
                  </Col>
                  <Col md={2} className="crop-cell">
                    <span className="paragraph-xs">{formatYield(crop.yieldTonsPerHa)}</span>
                  </Col>
                  <Col md={3} className="crop-cell">
                    <span className="paragraph-xs">{crop.seedingStartDate ? formatDayDate(crop.seedingStartDate) : "-"}</span>
                  </Col>
                  <Col md={3} className="crop-cell">
                    <span className="paragraph-xs">{crop.harvestEndDate ? formatDayDate(crop.harvestEndDate) : "-"}</span>
                  </Col>
                  <Col md={3} className="crop-cell">
                    <TagNormal color="grey" text={crop.cropType?.toLowerCase()} />
                  </Col>
                  <Col md={4} className="crop-cell">
                    <VerificationStatusDropdown status={crop.verificationStatus ?? null} cropId={crop.id} onStatusChange={handleStatusChange} />
                  </Col>
                  <Col md={2} className="crop-cell">
                    <span className="paragraph-xs">
                      {(() => {
                        const cropEditingRequests = getCropEditingRequests(crop.id);
                        return `${cropEditingRequests.filter((req) => req.completedAt).length}/${cropEditingRequests.length}`;
                      })()}
                    </span>
                  </Col>
                  <Col md={3} className="crop-cell">
                    {(() => {
                      const cropEditingRequests = getCropEditingRequests(crop.id);
                      const buttonState = getEditingRequestButtonState(cropEditingRequests);

                      return (
                        <Button
                          type={buttonState.type}
                          className={buttonState.className}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingRequestCrop(crop);
                          }}
                        >
                          {buttonState.text}
                        </Button>
                      );
                    })()}
                  </Col>
                </Row>
              ))}
            </div>
          );
        })}

        {/* Empty state */}
        {sortedYears.length === 0 && !isLoading && (
          <Row className="empty-state">
            <Col md={24}>
              <span className="paragraph-s text-disabled">No crops found for this field</span>
            </Col>
          </Row>
        )}
      </SkeletonWrapper>

      <EditingRequestModal
        crop={editingRequestCrop}
        year={missingCropRequestYear}
        fieldId={fieldId}
        showModal={!!(editingRequestCrop || missingCropRequestYear)}
        onClose={() => {
          setEditingRequestCrop(null);
          setMissingCropRequestYear(null);
        }}
        farmId={farmId}
        editingRequests={
          missingCropRequestYear
            ? editingRequests.filter((req) => req.fieldId === fieldId && req.year === missingCropRequestYear && !req.fieldCropId)
            : editingRequests
        }
        verificationYear={verificationYear}
      />

      <CropDrawer
        crop={cropToEdit}
        showDrawer={!!cropToEdit}
        onClose={() => setCropToEdit(null)}
        fieldPractices={fieldPractices}
        field={field}
        farmId={farmId}
      />
    </div>
  );
};

const AddCropButton = ({ onSelect }: { onSelect: (cropType: CROP_TYPE_FCPO) => void }) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <>
      <IconButton
        iconName="klim/plus"
        type="tertiary"
        size="small"
        className="margin-left-50"
        tooltip={{ title: "Add crop" }}
        onClick={() => setShowMenu(true)}
      />

      <ContextMenu
        items={[
          {
            iconName: "klim/crops-matured",
            label: "Main crop",
            onClick: () => onSelect(CROP_TYPE_FCPO.MAIN),
          },
          {
            iconName: "klim/crops-branching",
            label: "Cover crop",
            onClick: () => onSelect(CROP_TYPE_FCPO.COVER),
          },
          {
            iconName: "klim/crops-emerging",
            label: "Undersowing",
            onClick: () => onSelect(CROP_TYPE_FCPO.UNDERSOWING),
          },
        ]}
        placement="bottom-left"
        visible={showMenu}
        onClose={() => setShowMenu(false)}
      />
    </>
  );
};

const RefetchingOverlay = () => (
  <div className="crop-table-refetching-overlay">
    <Flex vertical={true} align="center" gap="1rem">
      <Spin size="large" />
    </Flex>
  </div>
);
